/* eslint-disable no-param-reassign */

import React from 'react';
import { useField } from 'react-final-form';

import { SUBSCRIPTION } from 'utils/formUtils';
import { roundToRange } from 'utils/global';

import { ASPECT_RATIO, INITIAL_BASE_WIDTH, SIGNATURE_BOX_RATIO, SIGNATURE_INFO_FONT_SIZE_PERCENTAGE, SIGNATURE_INFO_MIN_WIDTH_PERCENTAGE, SIGNATURE_INFO_TEXT_MARGIN_PERCENTAGE } from '../constants';
import type { Dimensions, ResizeData, SignatureData, Point, ZoneData, ResizeHandlers, MouseHandlers } from './useSignZoneTypes';

type UseSignZoneHandlersProps = {
  name: string;
  scale: number;
  hasSignZone: boolean;
  pdfRef: React.RefObject<HTMLCanvasElement | null>;
  tooltipRef: React.RefObject<HTMLDivElement | null>;
  onClick: (event?: React.MouseEvent) => void;
  signZoneRef: React.RefObject<HTMLDivElement | null>;
  onChange: (data: SignatureData) => void;
};

type UseSignZoneHandlersResponseType = {
  data: Partial<ZoneData>,
  resizeHandlers: ResizeHandlers,
  signZoneHandlers: MouseHandlers,
  isDragging: boolean;
  isResizing: boolean;
  minPageSize: number;
}

const computePointPosition = ({ x, y }: Point, { offsetX, offsetY }: Dimensions): Point => ({
  x: x / offsetX * 100,
  y: y / offsetY * 100,
});

const useSignZoneHandlers = ({
  name,
  onChange,
  hasSignZone,
  pdfRef,
  signZoneRef,
  tooltipRef,
  scale,
  onClick }: UseSignZoneHandlersProps): UseSignZoneHandlersResponseType => {
  const topLeftX = useField<number>(`${name}.topLeft.x`, SUBSCRIPTION.NULLABLE_VALUE_ONLY).input.value;
  const topLeftY = useField<number>(`${name}.topLeft.y`, SUBSCRIPTION.NULLABLE_VALUE_ONLY).input.value;
  const bottomRightX = useField<number>(`${name}.bottomRight.x`, SUBSCRIPTION.NULLABLE_VALUE_ONLY).input.value;
  const initialWidth = React.useMemo(() => INITIAL_BASE_WIDTH / scale, [scale]);
  const [pageSize, setPageSize] = React.useState({ width: 0, height: 0 });

  const signZoneInitialSize = React.useMemo(
    () => ({
      width: initialWidth,
      height: initialWidth * ASPECT_RATIO,
    }),
    [initialWidth]
  );

  // Total width and total height of the pdf page
  const pageWidth = pageSize.width / scale;
  const pageHeight = pageSize.height / scale;
  const minPageSize = Math.min(pageWidth, pageHeight);

  const [isDragging, setIsDragging] = React.useState(false);
  const [isResizing, setIsResizing] = React.useState(false);
  const [size, setSize] = React.useState({ width: SIGNATURE_BOX_RATIO[0], height: SIGNATURE_BOX_RATIO[1] });

  React.useEffect(() => {
    if (tooltipRef.current) {
      tooltipRef.current.style.scale = `${1 / scale}`;
    }

    if (signZoneRef.current) {
      signZoneRef.current.style.left = `${signZoneRef.current.style.left ?? 0 / scale}`;
      signZoneRef.current.style.top = `${signZoneRef.current.style.top ?? 0 / scale}`;
    }
  }, [scale, signZoneRef, tooltipRef]);

  React.useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        const boundingRec = pdfRef.current?.getBoundingClientRect();
        const { width: newWidth, height: newHeight } = boundingRec ?? {};
        setPageSize({ width: newWidth ?? 0, height: newHeight ?? 0 });
        // Delay to ensure the PDF is fully rendered (otherwise, the dimensions from getBoundingClientRect might not be accurate)
        if (hasSignZone) {
          const computedWidth = ((bottomRightX - topLeftX) * (newWidth ?? 0)) / 100;
          const computedHeight = computedWidth * ASPECT_RATIO;
          setSize({ width: computedWidth / scale, height: computedHeight / scale });
        }
      }, 200);
    });

    if (pdfRef.current) {
      resizeObserver.observe(pdfRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [pdfRef, topLeftX, bottomRightX, hasSignZone, scale]);

  const handleMouseUp = React.useCallback(() => {
    if (isDragging || isResizing) {
      setIsDragging(false);
      setIsResizing(false);

      if (!signZoneRef.current) {
        return;
      }

      // offsetLeft and offsetTop give the top left point from the PDF border
      // offsetHeight and offsetWidth give the height and the width of the signZone
      const { offsetLeft, offsetTop, offsetHeight, offsetWidth } = signZoneRef.current;

      // Compute topLeft point in percentage
      const { x: newTopLeftX, y: newTopLeftY } = computePointPosition({ x: offsetLeft, y: offsetTop }, { offsetX: pageWidth, offsetY: pageHeight });

      // Compute bottomRight point in percentage
      const { x: newBottomRightX, y: newBottomRightY } = computePointPosition(
        { x: (offsetLeft + offsetWidth), y: (offsetTop + offsetHeight) },
        { offsetX: pageWidth, offsetY: pageHeight }
      );

      onChange({
        topLeft: {
          x: roundToRange(newTopLeftX, 0, 100),
          y: roundToRange(newTopLeftY, 0, 100),
        },
        bottomRight: {
          x: roundToRange(newBottomRightX, 0, 100),
          y: roundToRange(newBottomRightY, 0, 100),
        },
      });
    }
  }, [isDragging, isResizing, onChange, pageHeight, pageWidth, signZoneRef]);

  const addSignZone = React.useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      if (!(event.target instanceof HTMLCanvasElement)) {
        return;
      }
      const { offsetX, offsetY, target } = event;
      // Total width and total height of the pdf page
      const { offsetWidth, offsetHeight } = target;

      // Ensure that the signZone fit in the page and center the signZone with the mouse position
      const x = Math.max(0, Math.min(offsetWidth - signZoneInitialSize.width, offsetX - signZoneInitialSize.width / 2));
      const y = Math.max(
        0,
        Math.min(offsetHeight - signZoneInitialSize.height, offsetY - signZoneInitialSize.height / 2)
      );

      // Compute X and Y in percentage
      const { x: newTopLeftX, y: newTopLeftY } = computePointPosition({ x, y }, { offsetX: offsetWidth, offsetY: offsetHeight });

      // Compute bottomRight point in percentage
      const { x: newBottomRightX, y: newBottomRightY } = computePointPosition(
        { x: (x + signZoneInitialSize.width), y: y + signZoneInitialSize.height },
        { offsetX: offsetWidth, offsetY: offsetHeight }
      );

      setSize(signZoneInitialSize);
      onChange({
        topLeft: {
          x: newTopLeftX,
          y: newTopLeftY,
        },
        bottomRight: {
          x: newBottomRightX,
          y: newBottomRightY,
        },
      });
      onClick?.();
    },
    [onChange, onClick, signZoneInitialSize]
  );

  const handleTooltipMove = React.useCallback(
    (event: MouseEvent) => {
      if (tooltipRef.current) {
        tooltipRef.current.classList.remove('d-none');

        const { offsetX, offsetY } = event;
        tooltipRef.current.style.scale = `${1 / scale}`;
        tooltipRef.current.style.top = `${offsetY - 20 / scale}px`;
        tooltipRef.current.style.left = `${offsetX + 20 / scale}px`;
      }
    },
    [tooltipRef, scale]
  );

  React.useEffect(() => {
    const canvas = pdfRef.current;
    if (hasSignZone) {
      canvas?.classList.remove('selection-cursor');

      // handle the mouseUp outside the signZone
      window.addEventListener('mouseup', handleMouseUp);
      // remove tooltip which follow the mouse
      canvas?.removeEventListener('mousemove', handleTooltipMove);
      // Remove the listener to add a signZone
      canvas?.removeEventListener('mouseup', addSignZone);
    } else {
      // reset state
      setIsDragging(false);
      setIsResizing(false);
      canvas?.classList.add('selection-cursor');

      // Add tooltip which follow the mouse
      canvas?.addEventListener('mousemove', handleTooltipMove);
      // Add a listener to add a signZone
      canvas?.addEventListener('mouseup', addSignZone);
      // Remove the listener which handle the mouseUp outside the signZone
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      canvas?.removeEventListener('mouseup', addSignZone);
      canvas?.removeEventListener('mousemove', handleTooltipMove);
    };
  }, [addSignZone, handleMouseUp, handleTooltipMove, hasSignZone, pdfRef]);

  const handleMouseDown = React.useCallback(() => {
    if (!isResizing) {
      // offsetLeft and offsetTop give the top left point from the PDF border
      setIsDragging(true);
    }
  }, [isResizing]);

  const handleMouseMove = React.useCallback(
    (event: React.MouseEvent) => {
      if (isDragging && !isResizing) {
        if (!pdfRef.current || !signZoneRef.current) {
          return;
        }

        // Calculate new position relative to the canvas
        const canvasRect = pdfRef.current.getBoundingClientRect();
        const signatureBoxWidth = signZoneRef.current.offsetWidth;
        const signatureBoxHeight = signZoneRef.current.offsetHeight;
        const infoBoxWidth = Math.max(SIGNATURE_INFO_MIN_WIDTH_PERCENTAGE * minPageSize, signatureBoxWidth);
        const infoBoxHeight = 2 * (SIGNATURE_INFO_FONT_SIZE_PERCENTAGE + SIGNATURE_INFO_TEXT_MARGIN_PERCENTAGE) * minPageSize;
        const newX = (event.clientX - canvasRect.left - size.width / 2) / scale;
        const newY = (event.clientY - canvasRect.top - size.height / 2) / scale;
        const maxX = Math.ceil(pageWidth - signatureBoxWidth - (infoBoxWidth - signatureBoxWidth) / 2);
        const maxY = Math.ceil(pageHeight - signatureBoxHeight - infoBoxHeight);

        // Compute the new position and ensure that the sign zone fit into the pdf page
        const clampedX = Math.min(Math.max((infoBoxWidth - signatureBoxWidth) / 2, newX), maxX);
        const clampedY = Math.min(Math.max(0, newY), maxY);

        // update the position
        signZoneRef.current.style.left = `${clampedX}px`;
        signZoneRef.current.style.top = `${clampedY}px`;
      }
    },
    [isDragging, isResizing, pdfRef, signZoneRef, pageHeight, pageWidth, scale, size, minPageSize]
  );

  const handleResizeStart = () => setIsResizing(true);
  const handleResize = (_: React.SyntheticEvent, { size: newSize }: ResizeData) => setSize(newSize);

  const data = React.useMemo(() => {
    if (hasSignZone) {
      return {
        top: topLeftY,
        left: topLeftX,
        ...size,
      };
    }
    return {};
  }, [hasSignZone, size, topLeftX, topLeftY]);

  const maxConstraints = React.useMemo(() => {
    const x = (pageWidth / 100) * topLeftX;
    const y = (pageHeight / 100) * topLeftY;

    const offsetX = pageWidth - x - size.width;
    const offsetY = pageHeight - y - size.height;

    if (offsetX <= offsetY) {
      const maxWidth = pageWidth - x;
      return maxWidth ? ([maxWidth, maxWidth * ASPECT_RATIO] as [number, number]) : undefined;
    }
    const maxHeight = pageHeight - y;
    return maxHeight ? ([maxHeight / ASPECT_RATIO, maxHeight] as [number, number]) : undefined;
  }, [pageWidth, topLeftX, pageHeight, topLeftY, size.width, size.height]);

  return {
    data,
    resizeHandlers: {
      maxConstraints,
      onResize: handleResize,
      onResizeStart: handleResizeStart,
    },
    signZoneHandlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
    },
    isDragging,
    isResizing,
    minPageSize,
  };
};

export default useSignZoneHandlers;
